import { InjectionToken, inject } from "@angular/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import {
  CapacitorLogbookFileStore,
  OpfsFilesystemAdapter,
  PersistentContainer,
  SqlJsCapacitorPersistenceModel,
} from "@newaydata/logbook-core";
import { MIGRATIONS, MigrationRunner } from "@newaydata/logbook-migrations";
import { SchemaRegistry } from "@newaydata/logbook-schemas";
import logbookManifest from "@newaydata/logbook-schemas/generated/manifest/logbook.manifest.json";
import { buildLogbookDb } from "@newaydata/logbook-schemas/generated/typescript/logbook.runtime";

import { PLATFORM_ADAPTER } from "../../core/platform/platform.adapter";

/** Builds the container `LogbookService` opens. Specs replace it to open a store in a directory of their own. */
export const LOGBOOK_CONTAINER = new InjectionToken<() => Promise<PersistentContainer>>("LOGBOOK_CONTAINER", {
  providedIn: "root",
  factory: () => {
    const platform = inject(PLATFORM_ADAPTER);
    return () => buildLogbookContainer(platform.isNative);
  },
});

/**
 * Build the logbook `PersistentContainer` for the running platform.
 *
 * The framework engine is the same everywhere — SQLite-in-WASM (sql.js). Only
 * the **filesystem** the `.sqlite` file lives in differs:
 *   - native: `@capacitor/filesystem` under `Directory.Data/logbooks` (resolved
 *     to an absolute `file://` URI so the plugin needs no `directory` arg);
 *   - web: `OpfsFilesystemAdapter` (the browser's OPFS, IndexedDB-backed shim
 *     where OPFS is unavailable), under `webDirectory`.
 *
 * The same injected filesystem backs both the persistence adapter (reads/writes
 * the db file) and the file store (lists/stats managed `.sqlite` files), so the
 * two always agree on the same physical files.
 */
export async function buildLogbookContainer(
  isNative: boolean,
  webDirectory = "logbooks"
): Promise<PersistentContainer> {
  const wasmBinary = await loadSqlJsWasm();
  const schemaRegistry = SchemaRegistry.fromManifest(logbookManifest);

  let filesystem;
  let containerStoreDirectory: string;
  if (isNative) {
    const { uri } = await Filesystem.getUri({ directory: Directory.Data, path: "logbooks" });
    filesystem = Filesystem;
    containerStoreDirectory = uri;
  } else {
    filesystem = new OpfsFilesystemAdapter();
    containerStoreDirectory = webDirectory;
  }

  return new PersistentContainer({
    adapterFactory: () => new SqlJsCapacitorPersistenceModel({ filesystem, initConfig: { wasmBinary } }),
    fileStore: new CapacitorLogbookFileStore(filesystem),
    containerStoreDirectory,
    primarySchemaId: "logbook",
    migrations: MIGRATIONS,
    migrationRunner: new MigrationRunner(),
    schemaRegistry,
    dbFactory: buildLogbookDb,
  });
}

/**
 * sql.js needs its WASM binary. Fetch it ourselves (rather than letting sql.js
 * resolve a URL) — the `capacitor://localhost/` scheme has known interop quirks
 * with sql.js's internal `locateFile`. `sql-wasm.wasm` is copied into `assets/`
 * by the Angular build (see `angular.json`).
 */
async function loadSqlJsWasm(): Promise<ArrayBuffer> {
  const res = await fetch("assets/sql-wasm.wasm");
  if (!res.ok) throw new Error(`logbook: sql-wasm.wasm fetch failed (${res.status})`);
  return res.arrayBuffer();
}
